// Share one coach, or the whole list, with a colleague (staff only; the server enforces it).
// Shows the shares you have given that cover this coach, each with an in-page Remove.
import React, { useEffect, useState } from 'react';
import { useCrm } from '../../lib/crm';

export default function ShareDialog({ relationshipId, onClose }) {
  const crm = useCrm();
  const [colleagues, setColleagues] = useState([]);
  const [given, setGiven] = useState([]);
  const [grantee, setGrantee] = useState('');
  const [scope, setScope] = useState('coach');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    Promise.all([crm.listColleagues(), crm.listShares()])
      .then(([c, s]) => { if (live) { setColleagues(c); setGiven(s.given || []); } })
      .catch(() => { if (live) setError('Could not load colleagues.'); });
    return () => { live = false; };
  }, [crm]);

  const relevant = given.filter((s) => s.relationship_id == null || s.relationship_id === relationshipId);

  const submit = async () => {
    if (!grantee || busy) return;
    setBusy(true); setError('');
    try {
      const s = await crm.createShare(Number(grantee), scope === 'coach' ? relationshipId : null);
      setGiven((g) => [...g, s]);
      setGrantee('');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not share.');
    }
    setBusy(false);
  };

  const remove = async (id) => {
    setError('');
    try {
      await crm.deleteShare(id);
      setGiven((g) => g.filter((s) => s.id !== id));
    } catch {
      setError('Could not remove that share.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel cr-modal" role="dialog" aria-modal="true" aria-labelledby="share-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 id="share-title" className="cr-modal__title">Share</h2>
        <div className="cr-form">
          <label className="adm-field cr-form__wide">
            <span className="adm-field__label">Colleague</span>
            <select className="adm-select" value={grantee} onChange={(e) => setGrantee(e.target.value)} data-testid="share_colleague">
              <option value="">Choose a colleague</option>
              {colleagues.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="cr-form__wide cr-radios" role="radiogroup" aria-label="What to share">
            <label><input type="radio" name="share_scope" checked={scope === 'coach'} onChange={() => setScope('coach')} /> Just this coach</label>
            <label><input type="radio" name="share_scope" checked={scope === 'list'} onChange={() => setScope('list')} /> My whole list</label>
          </div>
        </div>
        {error && <div role="alert" className="cr-error">{error}</div>}
        <div className="cr-modal__actions">
          <button type="button" className="adm-btn" onClick={onClose}>Close</button>
          <button type="button" className="adm-btn adm-btn--primary" disabled={!grantee || busy} onClick={submit} data-testid="share_submit">Share</button>
        </div>
        {relevant.length > 0 && (
          <div className="cr-shares">
            <h3 className="cr-section__title">Shared with</h3>
            <ul className="cr-list">
              {relevant.map((s) => (
                <li key={s.id} className="cr-list__row">
                  <span>{s.grantee.name} <span className="cr-sub">{s.relationship_id == null ? 'whole list' : 'this coach'}</span></span>
                  <button type="button" className="adm-btn adm-btn--sm adm-btn--danger" onClick={() => remove(s.id)}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
